import mixpanel from 'mixpanel-browser'
import { isPreviewMode } from '@/assetSource'
import type { MixpanelEvent, MixpanelProfile } from '@/services/mixpanel'
import type { User } from '@/stores/auth'
import { useAuthStore } from '@/stores/auth'

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN
const isDevelopment = process.env.NODE_ENV === 'development'
const isTest = process.env.NODE_ENV === 'test'
let hasInitialized = false

function buildProfile(user: User, organizationName?: string): MixpanelProfile {
  return {
    $email: user.email,
    $name: user.fullName,
    organization_name: organizationName,
    role: user.role,
    default_product: user.defaultProduct,
  }
}

export class MixpanelService {
  private declare unsubscribe: () => void

  constructor() {
    if (this.skipRequest || hasInitialized) return

    // Skip initialization if in PR preview mode
    if (isPreviewMode()) {
      console.info('Mixpanel: Disabled in PR preview mode')
      return
    }

    // Skip initialization if no token is configured
    if (!MIXPANEL_TOKEN) {
      console.error('Mixpanel: VITE_MIXPANEL_TOKEN not configured')
      return
    }

    mixpanel.init(MIXPANEL_TOKEN)
  }

  init() {
    if (hasInitialized) return

    hasInitialized = true

    this.setupUserSubscription()
  }

  get skipRequest() {
    return isDevelopment || isTest || isPreviewMode()
  }

  identify(userId: string, userProperties: MixpanelProfile = {}) {
    if (this.skipRequest) return

    mixpanel.identify(userId)
    mixpanel.people.set(userProperties)
  }

  track(event: MixpanelEvent, properties: Record<string, unknown> = {}) {
    if (this.skipRequest) return

    mixpanel.track(event, properties)
  }

  destroy() {
    this.unsubscribe?.()
  }

  private setupUserSubscription() {
    const { user, organization } = useAuthStore.getState()

    if (user) {
      this.identify(user.id, buildProfile(user, organization?.name))
    }

    this.unsubscribe = useAuthStore.subscribe(({ user, organization }) => {
      if (!user) return

      this.identify(user.id, buildProfile(user, organization?.name))
    })
  }
}

export default new MixpanelService()
